import { useState, useEffect } from 'react'
import { Footer, Nav } from './App'
// ─── HOOKS ────────────────────────────────────────────────────────────────────

function useWindowWidth(): number {
    const [w, setW] = useState(0)
    useEffect(() => {
        const fn = () => setW(window.innerWidth)
        fn()
        window.addEventListener('resize', fn)
        return () => window.removeEventListener('resize', fn)
    }, [])
    return w
}




function useActiveSection(ids: string[]) {
    const [active, setActive] = useState(ids[0])
    useEffect(() => {
        const handler = () => {
            const scrollPos = window.scrollY + 120
            for (let i = ids.length - 1; i >= 0; i--) {
                const el = document.getElementById(ids[i])
                if (el && el.offsetTop <= scrollPos) {
                    setActive(ids[i])
                    break
                }
            }
        }
        window.addEventListener('scroll', handler, { passive: true })
        return () => window.removeEventListener('scroll', handler)
    }, [ids])
    return active
}

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────

function GlobalStyles() {
    useEffect(() => {
        const s = document.createElement('style')
        s.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');
      *,*::before,*::after { box-sizing: border-box; }
      html { scroll-behavior: smooth; }
      body { margin: 0; padding: 0; background: #000; color: rgba(255,255,255,0.88); font-family: 'DM Sans', sans-serif; font-weight: 300; overflow-x: hidden; -webkit-font-smoothing: antialiased; }
      body::after { content: ''; position: fixed; inset: 0; pointer-events: none; z-index: 9998; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23g)' opacity='0.05'/%3E%3C/svg%3E"); opacity: 0.25; }
      ::-webkit-scrollbar { width: 5px; }
      ::-webkit-scrollbar-track { background: #000; }
      ::-webkit-scrollbar-thumb { background: rgba(37,89,244,0.35); border-radius: 10px; }
      ::-webkit-scrollbar-thumb:hover { background: rgba(37,89,244,0.6); }
      @keyframes riseIn { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes floatA { 0%,100% { transform: translate(0,0); } 50% { transform: translate(20px,-20px); } }
    `
        document.head.appendChild(s)
        return () => { document.head.removeChild(s) }
    }, [])
    return null
}




// ─── SECTION DATA ─────────────────────────────────────────────────────────────

const SECTIONS = [
    { id: 's1', num: '01', title: 'Acceptance of Terms' },
    { id: 's2', num: '02', title: 'The Service' },
    { id: 's3', num: '03', title: 'Accounts & Eligibility' },
    { id: 's4', num: '04', title: 'Content, AI & Licensing' },
    { id: 's5', num: '05', title: 'Advanced AI Systems' },
    { id: 's6', num: '06', title: 'Acceptable Use' },
    { id: 's7', num: '07', title: 'Payments & Subscriptions' },
    { id: 's8', num: '08', title: 'Teacher Platform' },
    { id: 's9', num: '09', title: 'Intellectual Property' },
    { id: 's10', num: '10', title: 'Disclaimer & Liability' },
    { id: 's11', num: '11', title: 'Termination & General' },
    { id: 's12', num: '12', title: 'Contact' },
]

// ─── SIDEBAR ──────────────────────────────────────────────────────────────────

function Sidebar() {
    const active = useActiveSection(SECTIONS.map(s => s.id))
    const [hovered, setHovered] = useState<string | null>(null)

    const scrollToSection = (id: string) => {
        const el = document.getElementById(id)
        if (el) window.scrollTo({ top: el.offsetTop - 90, behavior: 'smooth' })
    }

    return (
        <aside style={{ position: 'sticky', top: 90, height: 'fit-content', width: 240, flexShrink: 0 }}>
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase' as const, color: '#2559F4', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'block', width: 16, height: 1, background: '#2559F4' }} />
                Contents
            </div>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {SECTIONS.map(s => {
                    const isActive = active === s.id
                    const isHov = hovered === s.id
                    return (
                        <button
                            key={s.id}
                            data-hover
                            onClick={() => scrollToSection(s.id)}
                            onMouseEnter={() => setHovered(s.id)}
                            onMouseLeave={() => setHovered(null)}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, background: isActive ? 'rgba(37,89,244,0.08)' : isHov ? 'rgba(255,255,255,0.03)' : 'transparent', border: 'none', borderLeft: `2px solid ${isActive ? '#2559F4' : 'transparent'}`, borderRadius: '0 6px 6px 0', padding: '7px 10px', cursor: 'pointer', textAlign: 'left' as const, transition: 'background .2s, border-color .2s', width: '100%' }}
                        >
                            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, fontWeight: 500, color: isActive ? '#2559F4' : 'rgba(255,255,255,0.25)', minWidth: 22, transition: 'color .2s' }}>{s.num}</span>
                            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12.5, color: isActive ? '#fff' : isHov ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.38)', transition: 'color .2s', lineHeight: 1.3 }}>{s.title}</span>
                        </button>
                    )
                })}
            </nav>
        </aside>
    )
}

// ─── CONTENT COMPONENTS ───────────────────────────────────────────────────────

function SectionTitle({ num, title }: { num: string; title: string }) {
    return (
        <div style={{ marginBottom: 28 }}>
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase' as const, color: '#2559F4', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'block', width: 16, height: 1, background: '#2559F4' }} />
                {num}
            </div>
            <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(28px,3.5vw,40px)', fontWeight: 400, letterSpacing: '0.04em', color: '#fff', margin: 0, lineHeight: 1 }}>
                {title}
            </h2>
        </div>
    )
}

function P({ children }: { children: React.ReactNode }) {
    return (
        <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 15, lineHeight: 1.85, color: 'rgba(255,255,255,0.58)', fontWeight: 300, margin: '0 0 18px' }}>
            {children}
        </p>
    )
}

function SubHead({ children }: { children: React.ReactNode }) {
    return (
        <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.85)', letterSpacing: '0.01em', margin: '26px 0 10px' }}>
            {children}
        </div>
    )
}

function Highlight({ children }: { children: React.ReactNode }) {
    return (
        <div style={{ background: 'rgba(37,89,244,0.07)', border: '1px solid rgba(37,89,244,0.2)', borderLeft: '3px solid #2559F4', borderRadius: '0 10px 10px 0', padding: '14px 18px', margin: '20px 0', fontFamily: "'DM Sans',sans-serif", fontSize: 14, lineHeight: 1.75, color: 'rgba(255,255,255,0.72)', fontWeight: 300 }}>
            {children}
        </div>
    )
}

function Warning({ children }: { children: React.ReactNode }) {
    return (
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderLeft: '3px solid rgba(255,255,255,0.3)', borderRadius: '0 10px 10px 0', padding: '14px 18px', margin: '20px 0', fontFamily: "'DM Sans',sans-serif", fontSize: 13.5, lineHeight: 1.75, color: 'rgba(255,255,255,0.5)', fontWeight: 300, letterSpacing: '0.01em' }}>
            {children}
        </div>
    )
}

function BulletList({ items }: { items: string[] }) {
    return (
        <ul style={{ margin: '12px 0 20px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, fontFamily: "'DM Sans',sans-serif", fontSize: 14.5, lineHeight: 1.7, color: 'rgba(255,255,255,0.52)', fontWeight: 300 }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#2559F4', flexShrink: 0, marginTop: 9 }} />
                    {item}
                </li>
            ))}
        </ul>
    )
}

function Divider() {
    return <div style={{ height: 1, background: 'linear-gradient(90deg, rgba(37,89,244,0.3), rgba(255,255,255,0.06), transparent)', margin: '52px 0' }} />
}

// ─── HERO ─────────────────────────────────────────────────────────────────────

function Hero() {
    const w = useWindowWidth()
    const isMobile = w < 768
    return (
        <section style={{ padding: isMobile ? '100px 20px 60px' : '120px 52px 72px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                <div style={{ position: 'absolute', width: 600, height: 600, top: -200, right: -100, borderRadius: '50%', background: 'radial-gradient(circle, rgba(37,89,244,0.14) 0%, transparent 65%)', filter: 'blur(80px)', animation: 'floatA 20s ease-in-out infinite' }} />
                <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize: '44px 44px', maskImage: 'radial-gradient(ellipse 60% 50% at 70% 50%, black 20%, transparent 70%)', WebkitMaskImage: 'radial-gradient(ellipse 60% 50% at 70% 50%, black 20%, transparent 70%)' }} />
            </div>

            <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: 'rgba(37,89,244,0.08)', border: '1px solid rgba(37,89,244,0.25)', borderRadius: 100, padding: '5px 14px 5px 10px', marginBottom: 28, animation: 'riseIn .8s cubic-bezier(.16,1,.3,1) .1s both' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#2559F4', display: 'block' }} />
                    <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: 'rgba(255,255,255,0.6)' }}>Legal Document</span>
                </div>

                <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 'clamp(48px,10vw,72px)' : 'clamp(60px,7vw,96px)', fontWeight: 400, lineHeight: 0.92, letterSpacing: '0.04em', color: '#fff', margin: '0 0 24px', animation: 'riseIn .9s cubic-bezier(.16,1,.3,1) .2s both' }}>
                    Terms of<br />
                    <span style={{ background: 'linear-gradient(130deg, #7eb8ff 0%, #2559F4 45%, #1438b0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Service.</span>
                </h1>

                <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', animation: 'riseIn .9s cubic-bezier(.16,1,.3,1) .3s both' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12.5, color: 'rgba(255,255,255,0.3)' }}>Last Revised</span>
                        <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12.5, fontWeight: 500, color: 'rgba(255,255,255,0.65)', background: 'rgba(255,255,255,0.06)', padding: '3px 10px', borderRadius: 6 }}>April 11, 2026</span>
                    </div>
                    <span style={{ width: 1, height: 14, background: 'rgba(255,255,255,0.15)', display: 'block' }} />
                    <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12.5, color: 'rgba(255,255,255,0.3)' }}>FretPractice · Mobrio Studio</span>
                </div>
            </div>
        </section>
    )
}

// ─── MAIN CONTENT ─────────────────────────────────────────────────────────────

function Content() {
    const w = useWindowWidth()
    const isMobile = w < 768
    const showSidebar = w >= 1100

    const body = (
        <div style={{ flex: 1, minWidth: 0 }}>

            {/* 1 */}
            <section id="s1">
                <SectionTitle num="01" title="Acceptance of Terms & Updates" />
                <P>FretPractice provides access to its applications, websites, and related technologies, including FretPractice, Dhun, and the Teacher Platform (collectively, the "Service"). These Terms of Service ("Terms") form a legally binding agreement between you and FretPractice governing your access to and use of the Service.</P>
                <Highlight>By accessing, downloading, installing, or otherwise using any part of the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree to these Terms, you must immediately discontinue use of the Service.</Highlight>
                <P>We reserve the right to modify, update, or replace these Terms at any time to reflect changes in our technology, legal requirements, or business practices. Your continued use of the Service after such updates become effective constitutes your acceptance of the revised Terms.</P>
            </section>

            <Divider />

            {/* 2 */}
            <section id="s2">
                <SectionTitle num="02" title="The Service" />
                <P>FretPractice is an AI-powered ecosystem built to enhance how musicians practice, create, and learn music. It integrates structured practice systems, artificial intelligence-driven music tools (via Dhun), and mentor-led learning experiences into a unified platform designed for both beginners and advanced users.</P>

                <SubHead>2.1 Scope of Features</SubHead>
                <P>The Service may include features such as practice timers, metronome systems, timing accuracy analysis, fretboard training tools, progress tracking dashboards, AI-generated backing tracks, song generation, stem splitting, and instructor interaction tools.</P>
                <P>You understand that features may vary based on your device, subscription tier, geographic location, or technical limitations. Certain advanced features, especially those powered by AI, may evolve over time and may produce variable results.</P>

                <SubHead>2.2 Modifications</SubHead>
                <P>We reserve the right, at our sole discretion, to modify, suspend, or discontinue any part of the Service at any time, including features, functionalities, access levels, or data availability. Such changes may be made with or without prior notice.</P>
            </section>

            <Divider />

            {/* 3 */}
            <section id="s3">
                <SectionTitle num="03" title="Accounts & Eligibility" />
                <Highlight>You must be at least 9 years old to access or use the Service. If you are under the age of 9, you may only use the Service with the involvement, supervision, and consent of a parent or legal guardian.</Highlight>

                <SubHead>3.1 Account Responsibility</SubHead>
                <P>To access certain features, you may be required to register for an account and provide personal information such as your name and email address. You agree to provide accurate, current, and complete information during registration and to update such information as necessary.</P>
                <P>You are solely responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</P>
                <BulletList items={[
                    'Users under the age of 9 are prohibited from sharing sensitive personal information with other users.',
                    'FretPractice does not knowingly solicit or collect personal information from minors beyond what is necessary to provide the Service.',
                    'Any attempt by users to exploit, harm, or solicit minors may result in immediate termination and potential legal action.',
                ]} />

                <SubHead>3.2 Security</SubHead>
                <P>You agree to immediately notify FretPractice of any unauthorized use of your account or suspected breach of security. While we implement reasonable security measures, we shall not be liable for any loss or damage arising from your failure to safeguard your account information.</P>
            </section>

            <Divider />

            {/* 4 */}
            <section id="s4">
                <SectionTitle num="04" title="Content, AI Systems & Licensing" />
                <P>The Service enables you to upload, create, generate, and interact with various forms of content, including audio recordings, practice sessions, musical compositions, prompts, and AI-generated outputs (collectively, "Content").</P>

                <SubHead>4.1 User Content Rights</SubHead>
                <P>You retain ownership of any original Content that you upload or create using the Service. However, by submitting or generating Content through the Service, you grant FretPractice a worldwide, non-exclusive, royalty-free, sublicensable license to use, process, store, reproduce, and display such Content solely for the purposes of operating, maintaining, and improving the Service.</P>

                <SubHead>4.2 AI-Generated Outputs</SubHead>
                <P>The Service utilizes artificial intelligence and machine learning technologies to generate music, recommendations, and analytical insights ("Output"). Due to the inherent nature of AI systems:</P>
                <BulletList items={[
                    'Outputs may not be unique and may be similar or identical to outputs generated for other users.',
                    'Outputs may contain inaccuracies, unintended artifacts, or incomplete information.',
                    'Outputs may not be eligible for copyright protection under applicable laws.',
                ]} />
                <Highlight>Unless explicitly permitted, you agree not to use Outputs for commercial distribution, resale, licensing, or monetization. FretPractice reserves the right to define, restrict, or modify commercial usage rights at any time.</Highlight>

                <SubHead>4.3 AI Training & Improvement</SubHead>
                <P>To improve the performance, accuracy, and capabilities of our AI systems, we may use aggregated, anonymized, and non-identifiable data derived from user interactions, inputs, and outputs. We do not use your Content in a manner that publicly identifies you without your consent.</P>

                <SubHead>4.4 Restrictions on AI Usage</SubHead>
                <P>You agree not to misuse the AI capabilities of the Service. In particular, you must not:</P>
                <BulletList items={[
                    'Use the Service or its outputs to develop, train, or enhance competing AI systems.',
                    'Attempt to reverse engineer, extract, or replicate underlying models or algorithms.',
                    'Use AI-generated content in a misleading, deceptive, or harmful manner.',
                    'Generate content that impersonates or replicates another individual\'s identity, voice, or likeness without authorization.',
                ]} />
            </section>

            <Divider />

            {/* 5 */}
            <section id="s5">
                <SectionTitle num="05" title="Advanced AI Systems, Training & Licensing" />
                <P>FretPractice and Dhun rely on advanced artificial intelligence and machine learning technologies to enable core features such as music generation, practice analysis, timing accuracy detection, and personalized feedback.</P>
                <Highlight>By using the Service, you acknowledge that AI-generated results are produced through probabilistic models and may vary in accuracy, quality, and consistency depending on inputs, system conditions, and model updates.</Highlight>

                <SubHead>Use of Content for System Improvement</SubHead>
                <P>To enhance the quality, performance, and reliability of the Service, you agree that your interactions, inputs, and generated outputs may be used in an aggregated, anonymized, and non-identifiable manner for system improvement. This may include:</P>
                <BulletList items={[
                    'Improving timing detection, rhythm analysis, and feedback accuracy.',
                    'Enhancing music generation quality, structure, and genre alignment.',
                    'Optimizing recommendations, practice insights, and user experience.',
                ]} />

                <SubHead>Licensing of User Content to FretPractice</SubHead>
                <P>In order to operate, maintain, and improve the Service, you grant FretPractice and its affiliates a worldwide, non-exclusive, royalty-free, sublicensable license to use your Content. This includes the right to store, reproduce, process, analyze, and transform Content to enable features and functionality.</P>

                <SubHead>AI Output Ownership and Limitations</SubHead>
                <P>You acknowledge that outputs generated by the Service may not be unique and may be similar or identical to outputs generated for other users. FretPractice does not guarantee that any Output is eligible for copyright or exclusive ownership rights under applicable laws.</P>

                <SubHead>Voice, Likeness & Identity Protection</SubHead>
                <Highlight>You agree not to upload, generate, or use audio, voice, or musical content that impersonates, replicates, or mimics another individual's voice, style, or identity without explicit authorization. FretPractice reserves the right to remove, restrict, or disable any content that violates these principles.</Highlight>

                <SubHead>Feedback and Improvements</SubHead>
                <P>Any feedback, suggestions, ideas, or recommendations you provide regarding the Service will be considered non-confidential and may be used by FretPractice without restriction or obligation, including to improve or enhance the Service without providing compensation or attribution to you.</P>

                <SubHead>Restrictions on AI Misuse</SubHead>
                <P>You agree not to misuse or exploit the AI capabilities of the Service. You must not:</P>
                <BulletList items={[
                    'Use the Service or its Outputs to develop, train, or improve competing AI or machine learning systems.',
                    'Reverse engineer, extract, or attempt to replicate underlying models, datasets, or system behavior.',
                    'Use AI-generated content in a deceptive, misleading, or harmful manner, including impersonation or misrepresentation.',
                ]} />
            </section>

            <Divider />

            {/* 6 */}
            <section id="s6">
                <SectionTitle num="06" title="Acceptable Use & User Conduct" />
                <P>You are solely responsible for your use of the Service and all Content you submit, generate, or interact with. You agree to use the Service in a lawful, ethical, and responsible manner at all times.</P>
                <P>You must not:</P>
                <BulletList items={[
                    'Upload or generate content that infringes intellectual property or legal rights.',
                    'Use the Service for illegal, abusive, harmful, or fraudulent activities.',
                    'Interfere with, disrupt, or compromise the integrity or performance of the platform.',
                    'Attempt to gain unauthorized access to systems, data, or other user accounts.',
                    'Misrepresent your identity or impersonate any individual or entity.',
                ]} />
                <P>We reserve the right to monitor usage, investigate violations, remove content, and take appropriate enforcement actions, including account suspension or termination.</P>
            </section>

            <Divider />

            {/* 7 */}
            <section id="s7">
                <SectionTitle num="07" title="Payments, Subscriptions & Free Tier" />
                <P>Certain features of the Service may be offered on a paid or subscription basis, while others may be available for free with limitations.</P>

                <SubHead>7.1 Billing</SubHead>
                <P>By subscribing to a paid plan, you agree to provide valid payment information and authorize us (or our payment partners) to charge your selected payment method for applicable fees. Subscription fees may be billed on a recurring basis, depending on your chosen plan.</P>

                <SubHead>7.2 Free Tier</SubHead>
                <P>We may offer a free version of the Service with restricted access to features. You agree not to abuse or exploit the free tier, including by creating multiple accounts or using unauthorized methods to bypass limitations.</P>

                <SubHead>7.3 Refunds</SubHead>
                <Highlight>Unless explicitly stated otherwise, all payments are final and non-refundable. We reserve the right to determine refund eligibility at our sole discretion in exceptional circumstances.</Highlight>
            </section>

            <Divider />

            {/* 8 */}
            <section id="s8">
                <SectionTitle num="08" title="Teacher Platform & User Interactions" />
                <P>The Service may facilitate interactions between students, instructors, and other users through the Teacher Platform. These interactions may include mentorship, feedback, live sessions, or content sharing.</P>
                <Highlight>FretPractice acts solely as a platform provider and does not control, endorse, or guarantee the quality, accuracy, or outcomes of any user interactions. Any engagement with other users is at your own risk.</Highlight>
                <P>We are not responsible for disputes, disagreements, or outcomes arising from such interactions. Users are expected to behave respectfully and professionally within the platform.</P>
            </section>

            <Divider />

            {/* 9 */}
            <section id="s9">
                <SectionTitle num="09" title="Intellectual Property & Third-Party Services" />
                <P>All rights, title, and interest in the Service, including its software, design, features, branding, and underlying technology, are owned by FretPractice or its licensors and are protected by applicable intellectual property laws.</P>
                <P>You are granted a limited, non-transferable, non-exclusive license to use the Service for its intended purpose. You may not copy, modify, distribute, or exploit any part of the Service without prior written permission.</P>
                <P>The Service may integrate with third-party services such as payment processors, analytics tools, or cloud providers. These services operate under their own terms and policies, and FretPractice is not responsible for their actions, availability, or compliance.</P>

                <SubHead>9.1 Copyright Infringement & Content Removal</SubHead>
                <P>FretPractice respects intellectual property rights and expects users to do the same. If you believe that any content on the Service infringes your copyright, you may submit a written notice including:</P>
                <BulletList items={[
                    'Identification of the copyrighted work.',
                    'Description of the infringing content.',
                    'Your contact details.',
                    'A statement of good faith belief.',
                    'A statement of accuracy under applicable law.',
                ]} />
                <P>Upon receiving a valid complaint, we may remove or disable access to the allegedly infringing content and take appropriate action, including account suspension for repeat violations.</P>
            </section>

            <Divider />

            {/* 10 */}
            <section id="s10">
                <SectionTitle num="10" title="Disclaimer & Limitation of Liability" />
                <Warning>THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED.</Warning>
                <P>To the maximum extent permitted by law:</P>
                <BulletList items={[
                    'We do not guarantee uninterrupted, secure, or error-free operation.',
                    'AI-generated outputs may be inaccurate, incomplete, or unsuitable for specific purposes.',
                    'We are not responsible for any loss of data, content, or user-generated material.',
                ]} />
                <Warning>IN NO EVENT SHALL FretPractice (and/or) Mobrio Studio BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL.</Warning>
            </section>

            <Divider />

            {/* 11 */}
            <section id="s11">
                <SectionTitle num="11" title="Termination, Privacy & General Terms" />
                <P>We reserve the right to suspend or terminate your access to the Service at any time, with or without notice, if we believe you have violated these Terms or engaged in harmful behavior. Upon termination, your access to certain features or Content may be restricted or permanently removed.</P>
                <P>Your use of the Service is also governed by our <span style={{ color: '#2559F4', cursor: 'pointer' }} onClick={() => window.location.href = '/privacy'}>Privacy Policy</span>, which outlines how we collect, use, and protect your data. By using the Service, you consent to these practices.</P>
                <Highlight>These Terms are governed by the laws of India. Any disputes arising out of or relating to these Terms shall be subject to the exclusive jurisdiction of the courts located in India.</Highlight>

                <SubHead>11.1 Dispute Resolution & Arbitration</SubHead>
                <P>In the event of any dispute, claim, or controversy arising out of or relating to these Terms or the Service, you agree to first attempt to resolve the matter informally by contacting FretPractice.</P>
                <P>If the dispute is not resolved within a reasonable period, FretPractice may, at its discretion, require that such disputes be resolved through binding arbitration in accordance with applicable Indian arbitration laws. Arbitration shall be conducted by a neutral arbitrator in a confidential manner.</P>
                <P>To the extent permitted by law, you agree to waive any right to participate in class actions or representative proceedings.</P>
            </section>

            <Divider />

            {/* 12 */}
            <section id="s12">
                <SectionTitle num="12" title="Contact" />
                <P>For any questions, concerns, or legal inquiries regarding these Terms, please reach out to us:</P>
                <div style={{ background: '#0a0d17', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '28px 32px', display: 'inline-flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(37,89,244,0.12)', border: '1px solid rgba(37,89,244,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: '#2559F4' }}>
                            <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                        </svg>
                    </div>
                    <div>
                        <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 4, letterSpacing: '0.05em', textTransform: 'uppercase' as const }}>Email us at</div>
                        <a href="mailto:fretpractice@mobrio.studio" data-hover style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 15, fontWeight: 500, color: '#2559F4', textDecoration: 'none', cursor: 'pointer', transition: 'color .2s' }} onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.color = '#7eb8ff'} onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.color = '#2559F4'}>
                            fretpractice@mobrio.studio
                        </a>
                    </div>
                </div>

                {/* Also link to Privacy Policy */}
                <div style={{ marginTop: 32, padding: '20px 24px', background: 'rgba(37,89,244,0.05)', border: '1px solid rgba(37,89,244,0.15)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
                    <div>
                        <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>Also see our Privacy Notice</div>
                        <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12.5, color: 'rgba(255,255,255,0.3)' }}>How we collect, use, and protect your data.</div>
                    </div>
                    <button
                        data-hover
                        onClick={() => window.location.href = '/privacy'}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'none', border: '1px solid rgba(37,89,244,0.35)', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: '#2559F4', transition: 'background .2s, border-color .2s' }}
                        onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = 'rgba(37,89,244,0.1)'; b.style.borderColor = 'rgba(37,89,244,0.6)' }}
                        onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = 'none'; b.style.borderColor = 'rgba(37,89,244,0.35)' }}
                    >
                        View Privacy Notice
                        <svg viewBox="0 0 24 24" style={{ width: 13, height: 13, fill: 'currentColor' }}><path d="M4 11v2h12l-5.5 5.5 1.42 1.42L19.84 12l-7.92-7.92L10.5 5.5 16 11H4z" /></svg>
                    </button>
                </div>
            </section>

        </div>
    )

    return (
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: `0 ${isMobile ? '20px' : '52px'}`, paddingBottom: 120 }}>
            <div style={{ display: 'flex', gap: 72, alignItems: 'flex-start' }}>
                {showSidebar && <Sidebar />}
                {body}
            </div>
        </div>
    )
}


// ─── PAGE ─────────────────────────────────────────────────────────────────────

export default function TermsOfService() {
    useEffect(() => {
        const prev = document.title
        document.title = 'Terms of Service — FretPractice'
        const meta = document.querySelector('meta[name="description"]')
        const prevDesc = meta?.getAttribute('content') || ''
        meta?.setAttribute('content', 'Read the FretPractice Terms of Service to understand the rules, rights, and responsibilities when using our AI-powered guitar practice platform.')
        return () => { document.title = prev; meta?.setAttribute('content', prevDesc) }
    }, [])
    return (
        <>
            <GlobalStyles />
            <Nav />
            <Hero />
            <Content />
            <Footer />
        </>
    )
}